import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { THEME_STORAGE_KEY } from '@/shared/config'

/**
 * Global UI state — the whole store, for the whole project. It stays small on
 * purpose: if it starts growing, server data has leaked into it.
 *
 * Only UI preferences are persisted. No tokens, no personal data.
 */

export type Theme = 'light' | 'dark'

type UiState = {
  theme: Theme
  sidebarCollapsed: boolean
  setTheme: (theme: Theme) => void
  toggleSidebar: () => void
}

const applyTheme = (theme: Theme) => {
  document.documentElement.dataset.theme = theme
  document.documentElement.style.backgroundColor = theme === 'dark' ? '#0b0b0d' : '#ffffff'
}

const readInitialTheme = (): Theme =>
  document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      // The inline boot script already decided this before first paint; read
      // it back rather than deciding a second time and flashing.
      theme: readInitialTheme(),
      sidebarCollapsed: false,
      setTheme: (theme) => {
        applyTheme(theme)
        set({ theme })
      },
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
    }),
    {
      name: THEME_STORAGE_KEY,
      // Persisted as a bare string so the boot script can read it without JSON.
      storage: {
        getItem: (name) => {
          const value = localStorage.getItem(name)
          return value === null ? null : { state: { theme: value as Theme } }
        },
        setItem: (name, value) => localStorage.setItem(name, value.state.theme),
        removeItem: (name) => localStorage.removeItem(name),
      },
      partialize: (state) => ({ theme: state.theme }),
    },
  ),
)
